import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { rowActionLabel } from '../../lib/deleteLabel'
import { useLocalizedText } from '../../lib/localizedText'
import { exportRoles } from './api/rolesApi'
import Button from '../../components/ui/Button'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import { Menu } from 'primereact/menu'
import type { MenuItem } from 'primereact/menuitem'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import MobileSheetDropdown from '../../components/ui/MobileSheetDropdown'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { roleColumns } from './components/RoleColumns'
import RoleFormDialog from './components/RoleFormDialog'
import RoleGrantsDialog from './components/RoleGrantsDialog'
import { useRoles } from './hooks/useRoles'
import { useGrants } from './hooks/useGrants'
import type { Role, RoleUpsert } from './types'
import Badge from '../../components/ui/Badge'
import { InputSwitch } from 'primereact/inputswitch'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useProxyMode } from '../proxy/useProxyMode'
import './admin.css'

/**
 * Mobil kart kebab menüsü — switch dışındaki 2+ aksiyonu (Yetkiler/Düzenle/Sil) tek düğmede toplar
 * (v2 madde 1: mobil kartlarda üst satır rozet solda, aksiyonlar sağda; tek buton kalsaydı doğrudan
 * gösterilirdi). `Menu` popup deseni `ActivityTable`/`TicketTable` satır aksiyonlarıyla aynıdır.
 */
function RoleCardMenu({
  row,
  busy,
  name,
  onGrants,
  onEdit,
  onDelete,
  t,
}: Readonly<{
  row: Role
  /** Kartın GÖRÜNEN adı — kebab düğmesinin erişilebilir adına girer (bu bileşen `localized` almıyor). */
  name: string
  busy: boolean
  onGrants: (row: Role) => void
  onEdit: (row: Role) => void
  onDelete: (row: Role) => void
  t: TFunction
}>) {
  const menuRef = useRef<Menu>(null)
  const items: MenuItem[] = [
    { label: t('admin.roles.actions.grants'), icon: 'pi pi-shield', command: () => onGrants(row) },
    { label: t('admin.roles.actions.edit'), icon: 'pi pi-pencil', command: () => onEdit(row) },
    {
      label: t('admin.roles.actions.delete'),
      icon: 'pi pi-trash',
      className: 'ui-card-menu-item--danger',
      command: () => onDelete(row),
    },
  ]
  return (
    <>
      <Menu model={items} popup ref={menuRef} appendTo={document.body} />
      <Button
        icon="pi pi-ellipsis-v"
        rounded
        text
        disabled={busy}
        /* Erişilebilir ad KAYDI da söyler (THY AI review, 26.10.2026 — ikinci tur). Projenin kendi
   yardımcısı `rowActionLabel` kullanılır: ad boşsa adsız anahtara düşer. */
        aria-label={rowActionLabel(t, 'admin.roles.actions.menu', name)}
        onClick={(e) => menuRef.current?.toggle(e)}
      />
    </>
  )
}

/**
 * Admin → Rol yönetim ekranı (015). CRUD + durum toggle + soft-delete + role yetki atama.
 *
 * <p><b>Yetki filtresi</b> — "bu yetki hangi rollere verilmiş?": Yetki Yönetimi'ndeki "Verildiği
 * Roller" kolonunun (118) ters yönü. Süzme istemcidedir: yetki listesi her yetkinin rollerini zaten
 * taşıyor (`Grant.roles`), ayrıca istek atılmaz. Seçenekler yalnız AKTİF yetkilerdir — role aktif
 * atanmış bir yetki pasife çekilemediği (`GRANT_IN_USE`) için pasif yetki hep boş tablo verirdi.
 */
export default function RolesAdminPage() {
  const { t, i18n } = useTranslation()
  const localized = useLocalizedText()
  const { list, create, update, changeStatus, remove, saveGrants } = useRoles()
  const grants = useGrants()
  const toast = useToast()
  // Kısıtlı Yetkili asistan (126): ekran salt okunur — yeni/düzenle/sil yok, switch pasif; rolün
  // yetkileri yalnız Yetkiler penceresinde göründüğü için pencere görüntüleme kipinde açılır.
  const { readOnly } = useProxyMode()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Role | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [grantsFor, setGrantsFor] = useState<Role | null>(null)
  const [grantFilterId, setGrantFilterId] = useState<number | null>(null)

  const grantContent = grants.list.data?.content
  const activeGrants = useMemo(() => (grantContent ?? []).filter((g) => g.recordStatus === 1), [grantContent])
  // Arama Türkçe-farkında olmak ZORUNDA (Anayasa): yerel verilmezse PrimeReact tarayıcı yereliyle
  // küçültür ve "işlem" yazınca "İşlem…" eşleşmez (AssignmentPickList deseni).
  const filterLocale = i18n.language.startsWith('en') ? 'en' : 'tr'
  // Sunucu ada göre TR sıralı döner; EN'de etiket değiştiği için sıra etikete göre yeniden kurulur.
  const grantOptions = useMemo(
    () =>
      activeGrants
        .map((g) => ({ label: localized(g.nameTr, g.nameEn), value: g.id }))
        .sort((a, b) => a.label.localeCompare(b.label, filterLocale)),
    [activeGrants, localized, filterLocale],
  )
  const grantFilter = activeGrants.find((g) => g.id === grantFilterId) ?? null

  const roleContent = list.data?.content
  const rows = useMemo(() => {
    const all = roleContent ?? []
    if (!grantFilter) return all
    const granted = new Set(grantFilter.roles.map((r) => r.id))
    return all.filter((r) => granted.has(r.id))
  }, [roleContent, grantFilter])
  const submitting = create.isPending || update.isPending
  const busyId = changeStatus.isPending
    ? (changeStatus.variables?.id ?? null)
    : remove.isPending
      ? (remove.variables ?? null)
      : null
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  // Filtreli boş sonuç "Rol yok / yeni rol ekleyin" demez — roller var, yalnız bu yetki hiçbirine verilmemiş.
  const emptyState = grantFilter ? (
    <EmptyState title={t('admin.roles.empty.filteredTitle')} description={t('admin.roles.empty.filteredDesc')} />
  ) : (
    <EmptyState title={t('admin.roles.empty.title')} description={t('admin.roles.empty.desc')} />
  )

  const openCreate = () => {
    setEditing(null)
    setNameError(null)
    setFormOpen(true)
  }
  const openEdit = (row: Role) => {
    setEditing(row)
    setNameError(null)
    setFormOpen(true)
  }

  // Domaine özel ele alma ekranda kalır; optimistic-lock/generic toast'ı merkez (019) basar.
  const handleMutationError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_NAME') {
      setNameError(t('admin.roles.errors.duplicate'))
      return
    }
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      list.refetch()
    }
  }

  const onSubmit = (payload: RoleUpsert) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: { ...payload, modDate: editing.modDate } },
        {
          onSuccess: () => {
            notify('success', t('admin.roles.toast.updated'))
            setFormOpen(false)
          },
          onError: handleMutationError,
        },
      )
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          notify('success', t('admin.roles.toast.created'))
          setFormOpen(false)
        },
        onError: handleMutationError,
      })
    }
  }

  const onToggle = (row: Role, active: boolean) => {
    changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      {
        onSuccess: () => notify('success', t('admin.roles.toast.statusChanged')),
        onError: (err) => {
          const code = getApiErrorCode(err)
          if (code === 'ROLE_IN_USE') notify('error', t('admin.roles.errors.inUse'))
          else if (code === 'SYSTEM_ROLE_PROTECTED') notify('error', t('admin.roles.errors.systemRoleProtected'))
        },
      },
    )
  }

  const onDelete = (row: Role) => {
    confirmDialog({
      header: t('admin.roles.delete.title'),
      message: t('admin.roles.delete.confirm', { name: row.nameTr }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('admin.roles.delete.accept'),
      accept: () =>
        remove.mutate(row.id, {
          onSuccess: () => notify('success', t('admin.roles.toast.deleted')),
          onError: (err) => {
            const code = getApiErrorCode(err)
            if (code === 'ROLE_IN_USE') notify('error', t('admin.roles.errors.inUse'))
            else if (code === 'SYSTEM_ROLE_PROTECTED') notify('error', t('admin.roles.errors.systemRoleProtected'))
          },
        }),
    })
  }

  const onSaveGrants = (grantIds: number[]) => {
    if (!grantsFor) return
    saveGrants.mutate(
      { id: grantsFor.id, grantIds },
      {
        onSuccess: () => {
          notify('success', t('admin.roles.toast.grantsSaved'))
          setGrantsFor(null)
        },
        // Hata → merkezî toast (019); diyalog açık kalır, kullanıcı tekrar deneyebilir.
      },
    )
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <RoleFormDialog
        visible={formOpen}
        initial={editing}
        submitting={submitting}
        nameError={nameError}
        onClearNameError={() => setNameError(null)}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />
      <RoleGrantsDialog
        visible={grantsFor != null}
        role={grantsFor}
        // Görüntülemede pasif yetkiler de listelenir: role hâlâ atanmışsa okuyan onu da görmeli.
        grantOptions={readOnly ? (grantContent ?? []) : activeGrants}
        readOnly={readOnly}
        submitting={saveGrants.isPending}
        onSubmit={onSaveGrants}
        onHide={() => setGrantsFor(null)}
      />

      <PageHeader
        title={t('nav.admin.roles')}
        actions={
          <>
            {/* Yetki filtresi başlık şeridinde, Yenile'nin solunda (kullanıcı isteği 16.10.2026) —
                ekranın tek filtresi; ayrı bir filtre çubuğu tablonun üstünde boş bir şerit açıyordu.
                Seçim `showClear` (×) ile kalkar; "Tümü" seçeneği yok, seçilmemiş hâl yer tutucuyla okunur.
                Dar ekranda alttan açılan panelin karartması/kaydırma kilidi `MobileSheetDropdown`'da. */}
            <MobileSheetDropdown
              className="admin-grant-filter"
              value={grantFilter?.id ?? null}
              options={grantOptions}
              onChange={(e) => setGrantFilterId((e.value as number | null | undefined) ?? null)}
              placeholder={t('admin.roles.filter.grantPlaceholder')}
              aria-label={t('admin.roles.filter.grantLabel')}
              filter
              filterLocale={filterLocale}
              filterPlaceholder={t('admin.roles.filter.grantSearch')}
              emptyMessage={t('admin.roles.filter.grantEmpty')}
              emptyFilterMessage={t('admin.roles.filter.grantEmpty')}
              showClear
              // Veri yokken (ilk yükleme / ilk yükleme hatası) kapalı. `isError`'a BAĞLANMAZ: React
              // Query refetch hatasında eski veriyi tutup isError=true yapar — tablo filtreli kalırken
              // dropdown kilitlenir ve PrimeReact kapalı dropdown'da ×'i çizmez, seçim kaldırılamazdı.
              disabled={!grantContent}
              scrollHeight="28rem"
            />
            {/* İkisi de tazelenir: filtrenin kaynağı yetki listesinin `roles` alanıdır. Yalnız roller
                çekilseydi başka bir yöneticinin yaptığı atama ekrana gelmez, sunucuda süzülen Excel'e
                gelirdi — ekran ile dosya ayrışırdı. */}
            <RefreshButton onRefresh={() => Promise.all([list.refetch(), grants.list.refetch()])} />
            <ExcelExportButton
              onExport={() => exportRoles(grantFilter?.id)}
              disabled={rows.length === 0}
            />
            {!readOnly && (
              <Button icon="pi pi-plus" label={t('admin.roles.actions.new')} onClick={openCreate} />
            )}
          </>
        }
      />

      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && rows.length === 0 && emptyState}
          {!list.isLoading &&
            rows.map((row) => {
              const active = row.recordStatus === 1
              return (
                <div key={row.id} className="ui-card">
                  <div className="ui-card-top">
                    <Badge tone={active ? 'green' : 'neutral'}>
                      {active ? t('admin.roles.status.active') : t('admin.roles.status.passive')}
                    </Badge>
                    <div className="ui-card-actions ui-card-actions--compact">
                      <InputSwitch
                        className="admin-switch"
                        checked={active}
                        disabled={readOnly || busyId === row.id}
                        onChange={(e) => onToggle(row, Boolean(e.value))}
                        aria-label={row.nameTr}
                        tooltip={t('admin.roles.actions.toggle')}
                        tooltipOptions={{ position: 'bottom' }}
                      />
                      {/* Salt okunur kipte menüde yalnız Yetkiler kalırdı; tek eylem doğrudan gösterilir
                          (menü javadoc'undaki kural). */}
                      {readOnly ? (
                        <Button
                          icon="pi pi-key"
                          rounded
                          text
                          aria-label={rowActionLabel(t, 'admin.roles.actions.grants', localized(row.nameTr, row.nameEn))}
                          tooltip={t('admin.roles.actions.grants')}
                          tooltipOptions={{ position: 'bottom' }}
                          onClick={() => setGrantsFor(row)}
                        />
                      ) : (
                        <RoleCardMenu
                          row={row}
                          name={localized(row.nameTr, row.nameEn)}
                          busy={busyId === row.id}
                          onGrants={setGrantsFor}
                          onEdit={openEdit}
                          onDelete={onDelete}
                          t={t}
                        />
                      )}
                    </div>
                  </div>
                  <div className="ui-card-head">
                    <span className="ui-card-title">{localized(row.nameTr, row.nameEn)}</span>
                  </div>
                  {row.descriptionTr && <span className="ui-card-row">{row.descriptionTr}</span>}
                </div>
              )
            })}
        </div>
      ) : (
      <DataTable
        value={rows}
        loading={list.isLoading}
        sortField="nameTr"
        sortOrder={1}
        emptyMessage={emptyState}
      >
        {roleColumns({
          t,
          localized,
          busyId,
          onEdit: openEdit,
          onGrants: setGrantsFor,
          onToggle,
          onDelete,
          readOnly,
        })}
      </DataTable>
      )}
    </div>
  )
}
